import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
/**
 * Lightbox opened by clicking a SpeciesGallery thumbnail (species page) or
 * a DescriptionGallery thumbnail (description page) - shows the full-size
 * photo with previous/next navigation through the same set (arrow keys
 * work too), click-to-zoom to natural size, and a footer crediting the
 * author with a link back to the source post when there is one (a
 * description page's own extracted photos have neither - see
 * extractPostImages.js - so those parts of the footer just don't render).
 *
 * Same interaction/sizing model as champis-forum's PostImageLightbox (a
 * plain, uncompiled-source copy under this extension's own class names
 * rather than a shared import - see PaintingsGalleryModal's own docblock
 * for why: Flarum extensions can't import each other's JS source, and
 * reusing class names across extensions would bleed one's CSS into the
 * other's DOM). This used to instead request real browser Fullscreen on
 * open, matching champis.net's own gallery - dropped because that needs a
 * user gesture the browser is willing to honor, silently does nothing
 * otherwise (confirmed directly - the modal was left relying on
 * :fullscreen styling that never applied, visually broken), and this
 * plain "hug the photo, cap at the viewport, scroll if zoomed past it"
 * approach reaches the same "photo takes up most of the screen" result
 * without that dependency.
 */
export default class SpeciesGalleryModal extends Modal {
  index = 0;
  zoomed = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.index = this.attrs.index;
    this.zoomed = false;
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.onkeydown = this.onkeydown.bind(this);
    document.addEventListener('keydown', this.onkeydown);
  }
  onremove(vnode) {
    super.onremove(vnode);
    document.removeEventListener('keydown', this.onkeydown);
  }
  onkeydown(e) {
    if (e.key === 'ArrowLeft') this.previous();
    if (e.key === 'ArrowRight') this.next();
  }
  className() {
    return 'Modal--large SpeciesGalleryModal';
  }

  // '' rather than the photo's own author/name: .Modal-header is hidden
  // entirely (see forum.less) so nav buttons/footer can be positioned
  // against a header-free modal - the author credit lives in the footer
  // below instead, which (unlike a fullscreen-only footer this used to
  // need) is always on screen regardless of zoom.
  title() {
    return '';
  }
  content() {
    const photos = this.attrs.photos;
    const photo = photos[this.index];
    const hasMultiple = photos.length > 1;
    return m("div", {
      className: 'Modal-body SpeciesGalleryModal-body' + (this.zoomed ? ' SpeciesGalleryModal-body--zoomed' : '')
    }, hasMultiple && m(Button, {
      icon: "fas fa-chevron-left",
      className: "Button Button--icon SpeciesGalleryModal-nav SpeciesGalleryModal-nav--prev",
      onclick: () => this.previous(),
      "aria-label": app.translator.trans('champis-species.forum.gallery.previous')
    }), m("img", {
      src: photo.url,
      alt: photo.baseName,
      onclick: () => {
        this.zoomed = !this.zoomed;
      }
    }), hasMultiple && m(Button, {
      icon: "fas fa-chevron-right",
      className: "Button Button--icon SpeciesGalleryModal-nav SpeciesGalleryModal-nav--next",
      onclick: () => this.next(),
      "aria-label": app.translator.trans('champis-species.forum.gallery.next')
    }), (hasMultiple || photo.authorName || photo.discussionId || photo.caption) && m("div", {
      className: "SpeciesGalleryModal-footer"
    }, photo.caption && m("div", {
      className: "SpeciesGalleryModal-caption"
    }, photo.caption), m("div", {
      className: "SpeciesGalleryModal-meta"
    }, hasMultiple && m("span", {
      className: "SpeciesGalleryModal-count"
    }, this.index + 1, " / ", photos.length), photo.authorName && m("span", null, app.translator.trans('champis-species.forum.gallery.by', {
      name: photo.authorName
    })), photo.discussionId && m("a", {
      href: this.sourceUrl(photo)
    }, app.translator.trans('champis-species.forum.gallery.source')))));
  }
  sourceUrl(photo) {
    return photo.postNumber && photo.postNumber > 1 ? app.route('discussion.near', {
      id: photo.discussionId,
      near: photo.postNumber
    }) : app.route('discussion', {
      id: photo.discussionId
    });
  }
  previous() {
    const count = this.attrs.photos.length;
    this.index = (this.index - 1 + count) % count;
    this.zoomed = false;
    m.redraw();
  }
  next() {
    const count = this.attrs.photos.length;
    this.index = (this.index + 1) % count;
    this.zoomed = false;
    m.redraw();
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesGalleryModal', SpeciesGalleryModal);